// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
    InventoryAddItem,
    InventoryAddItemResponse,
    InventoryAdvancedSearchParams,
    InventoryBasicSearchParams,
    InventoryEditItem,
    InventoryList,
} from "../model/Inventory";
import { UserInfo } from "../model/User";

export interface CartItem {
    id?: string
    itemId: string
    webOrderDetailId: string;
    trackingNumber: string;
    serviceType: string;
    details: string;
    quantity: string;
    requestor: string;
    costCenter: string;
    customerComment: string;
    placedInCartDtime: string;
}

export type CartList = Array<CartItem>
export interface OrderItem {
    id?: string;
    webOrderDetailId: string;
    webOrderID: string;
    batchOrderNumber: string;
    trackingNumber: string;
    serviceType: string;
    details: string;
    quantity: string;
    status: string;
    statusDate: string;
    workorderID: string;
    requestor: string;
    customerWorkorderAccount: string;
    deletable: boolean;
}

export type OrderList = Array<OrderItem>
export interface UserSSO {
    success: boolean;
    message: string;
    errors?: {
        email?: string;
    };
}

export interface PasswordObject {
    email: string;
    password: string;
}

export interface ChangePasswordObject {
    currentPassword: string;
    newPassword: string;
}
export interface ForgotPasswordObject {
    email: string;
}
export interface ValidatePasswordTokenObject {
    token: string;
}
export interface ResetPasswordObject {
    vt: string;
    newPassword: string;
}

export interface ItemActivity {
    id: string;
    dateTime: string;
    activity: string;
    webOrderBatchID: string;
    workOrderID: string;
    workOrderCreateDT: string;
    workOrderRequiredByDT: string;
    webOrderTrackingID: string;
    comment: string;
}

export type ItemActivityList = Array<ItemActivity>

export interface ItemContents {
    id: string;
    type: string;
}

export type ItemContentsList = Array<ItemContents>

export interface AccountAction {
    id: string;
    code: string;
    description: string;
}

export interface AccountAddress {
    id: string;
    description: string;
    contact?: string;
    line1: string;
    line2?: string;
    line3?: string;
    phone?: string;
}

export interface AccountUpdateAddress {
    accountId: string;
    id?: string;
    description: string;
    contact?: string;
    line1: string;
    line2?: string;
    line3?: string;
    phone?: string;
    fax?: string;
}

export interface AccountRemoveAddress {
    accountId: string;
    id: string;
}

export interface AccountItem {
    id: string,
    path: string,
    code: string,
    name?: string,
    children?: AccountItemList;
    actionCodes?: Array<AccountAction>;
    addresses?: Array<AccountAddress>;
}

export type AccountItemList = Array<AccountItem>

export interface APIAdvancedSearchParam {
    code: string;
    name: string;
}

export interface APIAdvancedSearch {
    code: string;
    name: string;
    description: string;
    parameters: Array<APIAdvancedSearchParam>
}

export interface APIInventorySearchParams {
    fullRecord: boolean;
    fullResults: boolean;
    includeLongDesc?: boolean;
    includeContent?: boolean;
    searchText?: string;
    type: {
        container: boolean;
        file: boolean;
        tape: boolean;
    },
    status: {
        in: boolean;
        out: boolean;
        destroyed: boolean;
        pending: boolean;
        permout: boolean;
    },
    accounts: {
        level1?: string;
        level2?: string;
        level3?: string;
        subfolders: boolean;
    },

    addDate?: {
        from?: string;
        to?: string;
    };
    destroyDate?: {
        from?: string;
        to?: string;
    };

    statusDate?: {
        from?: string;
        to?: string;
    };

    contentsDate?: {
        from?: string;
        to?: string;
    };

    permanent: {
        yes: boolean;
        no: boolean;
    };
}

const buildBasicSearchParams = (params: InventoryBasicSearchParams) => {
    if (params === null) {
        return null;
    }

    const body:APIInventorySearchParams = {
        fullRecord: params.fullRecord,
        fullResults: params.fullResults,
        includeLongDesc: params.includeLongDesc,
        includeContent: params.includeContent,
        type: {
            container: params.type.container,
            file: params.type.file,
            tape: params.type.tape,
        },
        status: {
            in: params.status.in,
            out: params.status.out,
            pending: params.status.pending,
            permout: params.status.permout,
            destroyed: params.status.destroyed,
        },
        accounts: {
            subfolders: params.accounts.subfolders,
        },
        permanent: {
            yes: params.permanent.yes,
            no: params.permanent.no,
        }
    };

    if (params.searchText !== undefined && params.searchText !== '') {
        body.searchText = params.searchText;
    }

    if (params.addDate.from !== "" && params.addDate.to !== "") {
        body.addDate = params.addDate;
    }

    if (params.destroyDate.from !== "" && params.destroyDate.to !== "") {
        body.destroyDate = params.destroyDate;
    }

    if (params.statusDate.from !== "" && params.statusDate.to !== "") {
        body.statusDate = params.statusDate;
    }

    if (params.contentsDate.from !== "" && params.contentsDate.to !== "") {
        body.contentsDate = params.contentsDate;
    }

    if (params.accounts.level1 !== "") {
        body.accounts.level1 = params.accounts.level1;
    }

    if (params.accounts.level2 !== "") {
        body.accounts.level2 = params.accounts.level2;
    }

    if (params.accounts.level3 !== "") {
        body.accounts.level3 = params.accounts.level3;
    }

    return body;
};

export interface CartItemIdentifier {
    objectCode: string,
    objectStatus?: string,
    objectType?: string,
}

export interface AddToCartItem {
    serviceCode: string;
    items: Array<CartItemIdentifier>
    location?: string;
    quantity?: number;
    requestor?: string;
    costCenter?: string;
    customerComment?: string;
}

export interface CartCheckoutItem {
    accountLevel1: string;
    accountLevel2: string;
    accountLevel3: string;
    priority: string;
    addressId: string;
    notes: string;
}

export interface CartCheckoutResponse {
    receiptId: string;
}

export interface TempBarcode {
    barcode: string;
}

export interface FieldValidationBarcode {
    barcode: string;
}

export interface FieldValidationAltCode {
    altCode: string;
    accountLevel1: string;
    accountLevel2?: string;
    accountLevel3?: string;
}

export interface FieldValidationContentsRange {
    contentsRangeStart: string;
    contentsRangeEnd: string;
}

export interface FieldValidationRequest {
    field: string;
    value: FieldValidationBarcode | FieldValidationAltCode | FieldValidationContentsRange
}

export interface FieldValidationResponse {
    valid: boolean;
    error?: string;
}

export interface RecordSeriesCalculationRequest {
    value: {
        recordSeriesId: string;
        accountLevel1: string;
        accountLevel2: string;
        accountLevel3: string;
        contentsStartDate: string;
        contentsEndDate: string;
        customDate: string;
        addDate: string;
    }
    context: string;
}

export interface RecordSeriesCalculationResponse {
    valid: boolean;
    destroyDate: string;
    legalHold: number;
}

export interface FieldSearchCartonBarcode {
    barcode: string;
}

export interface FieldSearchCartonAltCode {
    altCode: string;
}

export interface FieldSearchSetName {
    setName: string | null;
    accountLevel1: string;
    accountLevel2?: string;
    accountLevel3?: string;
}

export interface FieldSearchContainerValue {
    id: string;
    barcode: string;
    altCode?: string;
    accountLevel1?: string; // account levels are optional to allow handling of OPENFILE
    accountLevel2?: string;
    accountLevel3?: string;
    duplicate?: boolean;
}

export interface FieldSearchCartonAltCodeRequest {
    field: string;
    value: FieldSearchCartonAltCode;
}

export interface FieldSearchSetNameValue {
    setName: string;
}

export interface FieldSearchRecordSeries {
    recordSeries: string;
    accountLevel1: string;
    accountLevel2?: string;
    accountLevel3?: string;
    type: string;
}

export interface FieldSearchRecordSeriesValue {
    recordSeriesId: string;
    recordSeriesCode: string;
    recordSeriesTitle: string;
    legalHold: boolean;
}

export interface NewsFeedItem {
    id: string;
    title: string;
    pubDate: string;
    description: string;
    image: string;
    content?: string;
}

export type NewsFeedList = Array<NewsFeedItem>

export interface OrgItem {
    name: string;
}

export type OrgItemList = Array<OrgItem>

export interface LoginToken {
    token: string;
}

// Define a service using a base URL and expected endpoints
export const graceApi = createApi({
    reducerPath: 'graceApi',
    baseQuery: fetchBaseQuery({ baseUrl: '/api/' }),
    tagTypes: ['Account', 'Accounts', 'AccountLookup', 'Addresses', 'User', 'Inventory', 'Cart', 'NewsFeed', 'Org'],
    endpoints: (builder) => ({
        getTopLevelAccounts: builder.query<AccountItemList, null>({
            query: () => `account`,
            providesTags: ["Accounts"],
        }),

        getOrgs: builder.query<OrgItemList, null>({
            query: () => `org`,
            providesTags: ["Org"],
        }),

        getAccount: builder.query<AccountItem, string | null>({
            query: (id: string | null) => `account/${id}`,
            providesTags: (result, error, id) => {
                if (result !== undefined && id) {
                    return [{type: "Account", id: id}]
                }
                if (error) {
                    console.warn(error);
                }
                return [];
            },
        }),

        getAccountLookup: builder.query<AccountItemList , string | null>({
            query: (param: string | null) => `/account/lookup/${param}`,
            providesTags: ['AccountLookup'],
        }),

        getAddresses: builder.query<Array<AccountAddress>, string>({
            query: (accountId: string) => `account/${accountId}/addresses`,
            providesTags: (result, error, accountId) => {
                if (result !== undefined && accountId) {
                    return [{type: "Addresses", id: accountId}]
                }
                return [];
            },
        }),

        getUserInfo: builder.query<UserInfo, void>({
            query: () => `user`,
            providesTags: ['User'],
            keepUnusedDataFor: 3600,
        }),

        checkSSO: builder.mutation<UserSSO, string>({
            query(email: string) {
                return {
                    url: `user/sso`,
                    method: 'POST',
                    headers: {
                        'content-type': 'application/x-www-form-urlencoded',
                    },
                    body: `email=${email}`,
                }
            },
        }),

        login: builder.mutation<void, PasswordObject>({
            query(credentials: PasswordObject) {
                // encode the password in case an ampersand etc is included
                const password = encodeURIComponent(credentials.password);
                return {
                    url: `user/login`,
                    method: 'POST',
                    headers: {
                        'content-type': 'application/x-www-form-urlencoded',
                    },
                    body: `email=${credentials.email}&password=${password}`,
                }
            },
        }),

        changePassword: builder.mutation<void, ChangePasswordObject>({
            query(newPasswordObj: ChangePasswordObject) {
                return {
                    url: `user/password`,
                    method: 'POST',
                    body: newPasswordObj,
                }
            },
            invalidatesTags: (result, error, arg) => {
                if (error) {
                    console.warn(error, arg);
                }
                return result ? ['User'] : [];
            },
        }),

        forgotPassword: builder.mutation<UserSSO, string>({
            query(email: string) {
                return {
                    url: `user/password`,
                    method: 'POST',
                    headers: {
                        // 'content-type': 'application/json',
                    },
                    body: `{ "email" : "${email}" }`,
                }
            },
        }),
        validatePasswordToken: builder.mutation<void, ValidatePasswordTokenObject>({
            query(tokenObj: ValidatePasswordTokenObject) {
                return {
                    url: `user/password`,
                    method: 'POST',
                    body: tokenObj,
                }
            },
            invalidatesTags: (result, error, arg) => {
                if (error) {
                    console.warn(error, arg);
                }
                return result ? ['User'] : [];
            },
        }),
        resetPassword: builder.mutation<void, ResetPasswordObject>({
            query(newPasswordObj: ResetPasswordObject) {
                return {
                    url: `user/password`,
                    method: 'POST',
                    body: newPasswordObj,
                }
            },
            invalidatesTags: (result, error, arg) => {
                if (error) {
                    console.warn(error, arg);
                }
                return result ? ['User'] : [];
            },
        }),

        getNewsFeed: builder.query<NewsFeedList, number>({
            query: (page_no) => {
                if (page_no === 1 || page_no === null) {
                    return 'services/news';
                } else {
                    return `services/news/paged/${page_no}`;
                }
            },
            providesTags: ['NewsFeed'],
            transformResponse: (response: NewsFeedList) => {
                const result: NewsFeedList = [];
                response.forEach(item => {
                    result.push(item);
                });
                return result;
            },
        }),
        getNewsFeedById: builder.query<NewsFeedItem, string >({
            query: (id) => ({
                url: `services/news/${id}`,
            }),
            providesTags: (result, error, id) => {
                if (result !== undefined) {
                    return [{type: "NewsFeed", id}]
                }
                return [];
            },
            transformResponse: (response: NewsFeedList) => {
                // Transform the response from an array to NewsFeedItem format
                const data: NewsFeedItem = response[0];
                return data;
            },
        }),

        getCart: builder.query<CartList, null>({
            query: () => `cart`,
            providesTags: ['Cart'],
            transformResponse: (response: CartList) => {
                const result:CartList = [];
                response.forEach(item => {
                    item.id = item.webOrderDetailId;
                    result.push(item);
                });
                return result;
            },
        }),

        getOrders: builder.query<OrderList, null>({
            query: () => `orders`,
            transformResponse: (response: OrderList) => {
                const result:OrderList = [];
                response.forEach(item => {
                    item.id = item.webOrderDetailId;
                    result.push(item);
                });
                return result;
            },
        }),

        getOrdersAll: builder.query<OrderList, null>({
            query: () => `orders/all`,
            transformResponse: (response: OrderList) => {
                const result:OrderList = [];
                response.forEach(item => {
                    item.id = item.webOrderDetailId;
                    result.push(item);
                });
                return result;
            },
        }),

        getItemsBasicSearch: builder.query<InventoryList, InventoryBasicSearchParams>({
            query: (request:InventoryBasicSearchParams) => ({
                url: `inventory`,
                method: 'POST',
                // Unpack the "accounts" object as we only want the ID for each level in the query.
                // We store the entire object in InventorySearchParams to ensure that the
                // account selector state is maintained between displays of the search dialog.
                body: buildBasicSearchParams(request),
            }),
            providesTags: ['Inventory'],
        }),

        getItemsAdvancedSearch: builder.query<InventoryList, InventoryAdvancedSearchParams>({
            query: (request:InventoryAdvancedSearchParams) => ({
                url: 'inventory/search',
                method: 'POST',
                body: request
            }),
            providesTags: ['Inventory'],
        }),

        getItemsFileSearch: builder.mutation<InventoryList, FormData>({
            query(data: FormData) {
                return {
                    url: `inventory`,
                    method: 'POST',
                    body: data,
                }
            },
            invalidatesTags: (result, error, arg) => {
                if (result !== undefined) {
                    return ["Inventory"];
                }
                if (error) {
                    console.warn(error, arg);
                }
                return [];
            },
        }),

        getItemById: builder.query<InventoryEditItem, string>({
            query: (id) => ({
                url: `inventory/${id}`,
            }),
            providesTags: (result, error, id) => {
                if (result !== undefined) {
                    return [{type: "Inventory", id}]
                }
                return [];
            },
        }),

        getItemActivity: builder.query<ItemActivityList, string>({
            query: (id) => ({
                url: `inventory/${id}/activity`,
            }),
            transformResponse: (response: ItemActivityList) => {
                const result:ItemActivityList = [];
                response.forEach(item => {
                    item.id = item.webOrderTrackingID;
                    result.push(item);
                });
                return result;
            },
        }),

        getCartonContents: builder.query<ItemContentsList, string>({
            query: (id) => ({
                url: `inventory/${id}/contents`,
            }),
        }),

        addToCart: builder.mutation<void, AddToCartItem>({
            query(item: AddToCartItem) {
                return {
                    url: `cart/add`,
                    method: 'POST',
                    body: item,
                }
            },
            invalidatesTags: (result, error, arg) => {
                if (result !== undefined) {
                    return ["Cart"];
                }
                if (error) {
                    console.warn(error, arg);
                }
                return [];
            },
        }),

        removeFromCart: builder.mutation<void, Array<string>>({
            query(items: Array<string>) {
                return {
                    url: `cart`,
                    method: 'DELETE',
                    body: items
                }
            },
            invalidatesTags: (result, error, arg) => {
                if (result !== undefined) {
                    return ["Cart"];
                }
                if (error) {
                    console.warn(error, arg);
                }
                return [];
            },
        }),

        checkoutCart: builder.mutation<CartCheckoutResponse, CartCheckoutItem>({
            query(item: CartCheckoutItem) {
                return {
                    url: `cart/checkout`,
                    method: 'POST',
                    body: item,
                }
            },
            invalidatesTags: (result, error, arg) => {
                if (result !== undefined) {
                    return ["Cart"];
                }
                if (error) {
                    console.warn(error, arg);
                }
                return [];
            },
        }),

        getTempBarcode: builder.query<TempBarcode, null>({
            // "unusedArg" simply exists so we can skip if needed,
            // but we can't use a skipToken as the call is conditional
            query: (unusedArg: null) => `inventory/barcode`,
            // Make the data expire quickly so subsequent calls aren't cached
            keepUnusedDataFor: 1,
        }),

        addItem: builder.mutation<InventoryAddItemResponse, InventoryAddItem>({
            query(item: InventoryAddItem) {
                return {
                    url: `inventory`,
                    method: 'POST',
                    body: {
                        ...item,
                        recordSeriesId: item.recordSeriesId === "" ? null : item.recordSeriesId,
                        containerId: item.containerId === "" ? null : item.containerId,
                        legalHold: Number(item.permanent),
                        permanent: Number(item.permanent),
                    },
                }
            },
        }),

        updateItem: builder.mutation<void, InventoryEditItem>({
            query(item: InventoryEditItem) {
                return {
                    url: `inventory/${item.id}`,
                    method: 'PUT',
                    body: {
                        ...item,
                        recordSeriesId: item.recordSeriesId === "" ? null : item.recordSeriesId,
                        containerId: item.containerId === "" ? null : item.containerId,
                        legalHold: Number(item.permanent),
                        permanent: Number(item.permanent),
                    },
                }
            },
            invalidatesTags: (result, error, arg) => {
                if (result !== undefined) {
                    return [{type: "Inventory", id: arg.id}];
                }
                if (error) {
                    console.warn(error, arg);
                }
                return [];
            },
        }),

        addAddress: builder.mutation<void, AccountUpdateAddress>({
            query(address: Omit<AccountUpdateAddress, "id">) {
                const {accountId} = address
                return {
                    url: `account/${accountId}/addresses`,
                    method: 'POST',
                    body: address,
                }
            },
            invalidatesTags: (result, error, {accountId}) => {
                if (result !== undefined) {
                    return [{type: "Addresses", id: accountId}, {type: "Account", id: accountId}];
                }
                return [];
            }
        }),

        updateAddress: builder.mutation<void, AccountUpdateAddress>({
            query(address: AccountUpdateAddress) {
                const {id, accountId} = address;
                return {
                    url: `account/${accountId}/addresses/${id}`,
                    method: 'PUT',
                    body: address,
                }
            },
            invalidatesTags: (result, error, {accountId}) => {
                if (result !== undefined) {
                    return [{type: "Addresses", id: accountId}, {type: "Account", id: accountId}];
                }
                return [];
            }
        }),

        removeAddress: builder.mutation<void, AccountRemoveAddress>({
            query({id, accountId}: AccountRemoveAddress) {
                return {
                    url: `account/${accountId}/addresses/${id}`,
                    method: 'DELETE',
                    body: {id}
                }
            },
            invalidatesTags: (result, error, {accountId}) => {
                if (result !== undefined) {
                    return [{type: "Addresses", id: accountId}, {type: "Account", id: accountId}];
                }
                return [];
            }
        }),

        validateField: builder.query<FieldValidationResponse, FieldValidationRequest>({
            query: (request) => ({
                url: `inventory/fieldvalidate`,
                method: 'POST',
                body: request,
            }),
        }),

        calculateRecordSeries: builder.query<RecordSeriesCalculationResponse, RecordSeriesCalculationRequest>({
            query: (request) => ({
                url: `inventory/fieldvalidate`,
                method: 'POST',
                body: {
                    field: "destroyDate",
                    ...request
                },
            }),
        }),

        searchContainerField: builder.query<FieldSearchContainerValue[], string | null>({
            query: (request) => ({
                url: `inventory/fieldsearch`,
                method: 'POST',
                body: {
                    field: 'container',
                    value: {
                        container: request ?? '',
                    }
                },
            }),
        }),

        searchSetNameField: builder.query<FieldSearchSetNameValue[], FieldSearchSetName>({
            query: (request) => ({
                url: `inventory/fieldsearch`,
                method: 'POST',
                body: {
                    field: 'setName',
                    value: {
                        ...request,
                        setName: request.setName ?? '',
                    }
                },
            }),
        }),

        searchRecordSeriesField: builder.query<FieldSearchRecordSeriesValue[], FieldSearchRecordSeries>({
            query: (request) => ({
                url: `inventory/fieldsearch`,
                method: 'POST',
                body: {
                    field: 'recordSeries',
                    value: {
                        ...request,
                        recordSeries: request.recordSeries ?? '',
                    }
                }
            }),
            keepUnusedDataFor: 0, // ensure a new request is made every time to properly handle form interactions
        }),
    }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetTopLevelAccountsQuery,
    useGetOrgsQuery,
    useGetAccountQuery,
    useGetAccountLookupQuery,
    useCheckSSOMutation,
    useLoginMutation,
    useChangePasswordMutation,
    useForgotPasswordMutation,
    useValidatePasswordTokenMutation,
    useResetPasswordMutation,
    useGetNewsFeedQuery,
    useGetNewsFeedByIdQuery,
    useGetCartQuery,
    useGetOrdersQuery,
    useGetOrdersAllQuery,
    useGetItemsBasicSearchQuery,
    useGetItemsAdvancedSearchQuery,
    useGetItemsFileSearchMutation,
    useGetItemByIdQuery,
    useGetItemActivityQuery,
    useGetCartonContentsQuery,
    useGetUserInfoQuery,
    useAddToCartMutation,
    useRemoveFromCartMutation,
    useCheckoutCartMutation,
    useGetTempBarcodeQuery,
    useAddItemMutation,
    useUpdateItemMutation,
    useGetAddressesQuery,
    useAddAddressMutation,
    useUpdateAddressMutation,
    useRemoveAddressMutation,
    useValidateFieldQuery,
    useCalculateRecordSeriesQuery,
    useSearchContainerFieldQuery,
    useSearchSetNameFieldQuery,
    useSearchRecordSeriesFieldQuery,
    usePrefetch
} = graceApi
